import { useState, useEffect } from "react";
import QuoteCarousel from "@/components/QuoteCarousel";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { UserButton, useUser } from "@clerk/react";
import {
  listPayoutBatches,
  listPools,
  getPayoutReady,
  createPayoutBatch,
  getPayoutBatch,
  confirmPayoutBatch,
  approvePayoutBatch,
  rejectPayoutBatch,
  getPayoutBatchTransfers,
  type PayoutBatch,
  type PayoutBatchDetail,
  type PayoutReadyDebt,
  type CreditorTransfer,
} from "@workspace/api-client-react";
import { LogoMark } from "@/components/Logo";
import {
  ArrowLeft,
  Plus,
  Loader2,
  CheckCircle,
  Clock,
  DollarSign,
  Send,
  FileText,
  AlertTriangle,
  ChevronRight,
  BadgeCheck,
  ShieldCheck,
  XCircle,
  UserCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

const METHOD_LABELS: Record<string, string> = {
  wire: "Wire Transfer",
  ach: "ACH",
  check: "Check",
  other: "Other",
};

const STATUS_BADGE: Record<string, { color: string; label: string }> = {
  pending_approval: { color: "bg-blue-100 text-blue-800 border-blue-200", label: "Awaiting Approval" },
  pending: { color: "bg-yellow-100 text-yellow-800 border-yellow-200", label: "Processing" },
  sent: { color: "bg-yellow-100 text-yellow-800 border-yellow-200", label: "Payment Sent" },
  confirmed: { color: "bg-green-100 text-green-800 border-green-200", label: "Confirmed" },
  failed: { color: "bg-red-100 text-red-800 border-red-200", label: "Failed" },
  rejected: { color: "bg-red-100 text-red-800 border-red-200", label: "Rejected" },
};

function fmt(n: number) {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function fmtDate(s: string) {
  return new Date(s).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

// ── Create Batch Dialog ────────────────────────────────────────────────────────
interface CreateBatchDialogProps {
  open: boolean;
  onClose: () => void;
}

function CreateBatchDialog({ open, onClose }: CreateBatchDialogProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [step, setStep] = useState<"select-pool" | "select-debts" | "details">("select-pool");
  const [selectedPoolId, setSelectedPoolId] = useState<number | null>(null);
  const [selectedDebtIds, setSelectedDebtIds] = useState<Set<number>>(new Set());
  const [paymentMethod, setPaymentMethod] = useState<"wire" | "ach" | "check" | "other">("ach");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [bankingAcknowledged, setBankingAcknowledged] = useState(false);

  const { data: pools } = useQuery({
    queryKey: ["pools"],
    queryFn: () => listPools(),
  });

  const { data: payoutReady, isLoading: loadingDebts } = useQuery({
    queryKey: ["payout-ready", selectedPoolId],
    queryFn: () => getPayoutReady(selectedPoolId!),
    enabled: selectedPoolId !== null,
  });

  const createMutation = useMutation<PayoutBatch, Error & { data?: { error?: string } }, void>({
    mutationFn: () =>
      createPayoutBatch({
        poolId: selectedPoolId!,
        debtIds: Array.from(selectedDebtIds),
        paymentMethod,
        reference: reference.trim() || undefined,
        notes: notes.trim() || undefined,
        paymentDate: paymentDate || undefined,
      }),
    onSuccess: (batch) => {
      toast({ title: "Payout batch submitted for approval", description: `Batch #${batch.id} — ${fmt(batch.totalAmount)} for ${batch.debtsCount} creditors is awaiting a second admin's approval before funds are released.` });
      void qc.invalidateQueries({ queryKey: ["payout-batches"] });
      void qc.invalidateQueries({ queryKey: ["pools"] });
      handleClose();
    },
    onError: (err: Error & { data?: { error?: string } }) => {
      toast({ title: "Failed to create batch", description: err?.data?.error ?? err.message, variant: "destructive" });
    },
  });

  function handleClose() {
    setStep("select-pool");
    setSelectedPoolId(null);
    setSelectedDebtIds(new Set());
    setPaymentMethod("ach");
    setReference("");
    setNotes("");
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setBankingAcknowledged(false);
    onClose();
  }

  const unverifiedInSelection = Array.from(selectedDebtIds).filter((id) => {
    const debt = payoutReady?.debts.find((d: PayoutReadyDebt) => d.id === id);
    return debt && debt.creditorBankingStatus?.verificationStatus !== "verified";
  }).length;

  function toggleDebt(id: number) {
    setSelectedDebtIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const eligiblePools = (pools?.items ?? []).filter((p) => ["active", "funded"].includes(p.status));
  const totalSelected = Array.from(selectedDebtIds).reduce((sum, id) => {
    const d = payoutReady?.debts.find((x: PayoutReadyDebt) => x.id === id);
    return sum + (d ? (d.effectiveAmount ?? d.balance) : 0);
  }, 0);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step === "select-pool" && "Step 1 — Select Pool"}
            {step === "select-debts" && "Step 2 — Select Debts"}
            {step === "details" && "Step 3 — Payment Details"}
          </DialogTitle>
        </DialogHeader>

        {step === "select-pool" && (
          <div className="space-y-3">
            {eligiblePools.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No pools with eligible debts right now.</p>
            ) : (
              eligiblePools.map((pool) => (
                <button
                  key={pool.id}
                  className={`w-full text-left p-3 rounded-xl border transition-colors ${
                    selectedPoolId === pool.id ? "border-primary bg-primary/5" : "hover:bg-muted/40"
                  }`}
                  onClick={() => setSelectedPoolId(pool.id)}
                >
                  <div className="font-medium">{pool.name}</div>
                  <div className="text-sm text-muted-foreground">
                    Balance: <span className="font-medium text-foreground">{fmt(Number(pool.balance))}</span>
                    {" · "}Status: <span className="capitalize">{pool.status}</span>
                  </div>
                </button>
              ))
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={handleClose}>Cancel</Button>
              <Button
                disabled={selectedPoolId === null}
                onClick={() => setStep("select-debts")}
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "select-debts" && (
          <div className="space-y-3">
            {loadingDebts ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : !payoutReady?.debts.length ? (
              <p className="text-sm text-muted-foreground text-center py-6">No eligible debts in this pool.</p>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Select the debts to include in this payout batch.
                </p>
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {payoutReady.debts.map((debt: PayoutReadyDebt) => (
                    <button
                      key={debt.id}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedDebtIds.has(debt.id) ? "border-primary bg-primary/5" : "hover:bg-muted/40"
                      }`}
                      onClick={() => toggleDebt(debt.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-sm">{debt.creditor}</div>
                          <div className="text-xs text-muted-foreground">
                            {debt.accountHolderName && `${debt.accountHolderName} · `}
                            {debt.accountNumber && `Acct: ${debt.accountNumber}`}
                          </div>
                        </div>
                        <div className="text-right flex flex-col items-end gap-1">
                          <div className="font-semibold text-sm">{fmt(debt.effectiveAmount ?? debt.balance)}</div>
                          <div className="flex gap-1 flex-wrap justify-end">
                            {debt.creditorBankingStatus?.verificationStatus === "verified" ? (
                              <Badge variant="outline" className="text-xs text-green-700 border-green-300">Banking Verified</Badge>
                            ) : debt.creditorBankingStatus?.verificationStatus === "pending" ? (
                              <Badge variant="outline" className="text-xs text-blue-700 border-blue-300">Pending Deposits</Badge>
                            ) : debt.creditorBankingStatus?.inviteSentAt ? (
                              <Badge variant="outline" className="text-xs text-orange-700 border-orange-300">Invite Sent</Badge>
                            ) : debt.creditorBankingStatus ? (
                              <Badge variant="outline" className="text-xs text-gray-600 border-gray-300">Not Submitted</Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs text-gray-400 border-gray-200">No Invite</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="rounded-lg bg-muted/30 p-3 text-sm">
                  <span className="font-medium">{selectedDebtIds.size}</span> debts selected ·{" "}
                  <span className="font-medium">{fmt(totalSelected)}</span> total
                </div>
              </>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep("select-pool")}>Back</Button>
              <Button
                disabled={selectedDebtIds.size === 0}
                onClick={() => setStep("details")}
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "details" && (
          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/20 p-3 text-sm space-y-1">
              <div>Pool: <span className="font-medium">{payoutReady?.pool.name}</span></div>
              <div>Debts: <span className="font-medium">{selectedDebtIds.size}</span></div>
              <div>Total: <span className="font-medium">{fmt(totalSelected)}</span></div>
            </div>

            <div className="space-y-2">
              <Label>Payment Method</Label>
              <div className="grid grid-cols-2 gap-2">
                {(["wire", "ach", "check", "other"] as const).map((m) => (
                  <button
                    key={m}
                    className={`p-2 rounded-lg border text-sm font-medium transition-colors ${
                      paymentMethod === m ? "border-primary bg-primary/5 text-primary" : "hover:bg-muted/40"
                    }`}
                    onClick={() => setPaymentMethod(m)}
                  >
                    {METHOD_LABELS[m]}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="paymentDate">Date Payment Sent</Label>
              <Input
                id="paymentDate"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
              />
              <p className="text-xs text-muted-foreground">The actual date you sent or initiated the payment to the creditor.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reference">Reference / Transaction ID</Label>
              <Input
                id="reference"
                placeholder="e.g. wire reference, check number…"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Internal Notes</Label>
              <Textarea
                id="notes"
                placeholder="Optional notes for the record…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>

            {unverifiedInSelection > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
                <div className="flex gap-2 text-sm text-amber-900">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                  <p>
                    <span className="font-semibold">{unverifiedInSelection} debt{unverifiedInSelection !== 1 ? "s" : ""} with unverified banking</span>
                    {" "}— the creditor's bank account has not been verified. Payment may fail or be delayed.
                  </p>
                </div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 accent-amber-600"
                    checked={bankingAcknowledged}
                    onChange={(e) => setBankingAcknowledged(e.target.checked)}
                  />
                  <span className="text-xs text-amber-800">
                    I acknowledge that unverified banking details may cause payment failure and accept responsibility for proceeding.
                  </span>
                </label>
              </div>
            )}

            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep("select-debts")}>Back</Button>
              <Button
                onClick={() => createMutation.mutate()}
                disabled={createMutation.isPending || (unverifiedInSelection > 0 && !bankingAcknowledged)}
              >
                {createMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Create Payout Batch
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Batch Detail Dialog ───────────────────────────────────────────────────────
interface BatchDetailDialogProps {
  batchId: number | null;
  onClose: () => void;
}

const TRANSFER_STATUS_CFG: Record<CreditorTransfer["status"], { label: string; color: string; icon: React.ReactNode }> = {
  pending: { label: "Pending", color: "text-gray-600 bg-gray-50 border-gray-200", icon: <Clock className="h-3 w-3 mr-1" /> },
  in_transit: { label: "In Transit", color: "text-blue-700 bg-blue-50 border-blue-200", icon: <Send className="h-3 w-3 mr-1" /> },
  paid: { label: "Paid", color: "text-green-700 bg-green-50 border-green-200", icon: <CheckCircle className="h-3 w-3 mr-1" /> },
  failed: { label: "Failed", color: "text-red-700 bg-red-50 border-red-200", icon: <XCircle className="h-3 w-3 mr-1" /> },
};

function BatchDetailDialog({ batchId, onClose }: BatchDetailDialogProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [confirming, setConfirming] = useState(false);

  const { data: batch, isLoading } = useQuery<PayoutBatchDetail>({
    queryKey: ["payout-batch", batchId],
    queryFn: () => getPayoutBatch(batchId!),
    enabled: batchId !== null,
  });

  const { data: transfers } = useQuery<CreditorTransfer[]>({
    queryKey: ["payout-batch-transfers", batchId],
    queryFn: () => getPayoutBatchTransfers(batchId!),
    enabled: batchId !== null,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const hasInFlight = data.some((t: CreditorTransfer) => t.status === "pending" || t.status === "in_transit");
      return hasInFlight ? 10000 : false;
    },
  });

  const [confirmResult, setConfirmResult] = useState<{
    debtsMarkedPaid: number;
    donorAcknowledgmentsSent: number;
    skipped1099cDebtIds: number[];
  } | null>(null);

  const confirmMutation = useMutation<
    PayoutBatch & { debtsMarkedPaid: number; donorAcknowledgmentsSent: number; skipped1099cDebtIds: number[] },
    Error & { data?: { error?: string } },
    void
  >({
    mutationFn: () => confirmPayoutBatch(batchId!),
    onSuccess: (result) => {
      setConfirmResult({
        debtsMarkedPaid: result.debtsMarkedPaid,
        donorAcknowledgmentsSent: result.donorAcknowledgmentsSent,
        skipped1099cDebtIds: result.skipped1099cDebtIds ?? [],
      });
      void qc.invalidateQueries({ queryKey: ["payout-batches"] });
      void qc.invalidateQueries({ queryKey: ["payout-batch", batchId] });
      void qc.invalidateQueries({ queryKey: ["pools"] });
      setConfirming(false);
    },
    onError: (err: Error & { data?: { error?: string } }) => {
      toast({ title: "Confirmation failed", description: err?.data?.error ?? err.message, variant: "destructive" });
      setConfirming(false);
    },
  });

  if (!batchId) return null;

  const statusCfg = batch ? (STATUS_BADGE[batch.status] ?? STATUS_BADGE.sent) : null;
  const allConfirmed = batch?.debts.every((d: PayoutBatchDetail["debts"][0]) => d.payoffConfirmationUrl) ?? false;

  return (
    <Dialog open={batchId !== null} onOpenChange={(o) => { if (!o) { setConfirming(false); setConfirmResult(null); onClose(); } }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isLoading ? "Loading…" : `Payout Batch #${batch?.id}`}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : batch ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Pool</div>
                <div className="font-medium">{batch.pool?.name ?? `#${batch.poolId}`}</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Total Amount</div>
                <div className="font-medium">{fmt(batch.totalAmount)}</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Payment Method</div>
                <div className="font-medium">{METHOD_LABELS[batch.paymentMethod] ?? batch.paymentMethod}</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Status</div>
                {statusCfg && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${statusCfg.color}`}>
                    {statusCfg.label}
                  </span>
                )}
              </div>
              {batch.reference && (
                <div className="rounded-lg border p-3 col-span-2">
                  <div className="text-xs text-muted-foreground">Reference</div>
                  <div className="font-mono text-sm">{batch.reference}</div>
                </div>
              )}
              {batch.notes && (
                <div className="rounded-lg border p-3 col-span-2">
                  <div className="text-xs text-muted-foreground">Notes</div>
                  <div className="text-sm">{batch.notes}</div>
                </div>
              )}
            </div>

            <div>
              <h4 className="font-medium text-sm mb-2">Included Debts ({batch.debts.length})</h4>
              <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                {batch.debts.map((debt: PayoutBatchDetail["debts"][0]) => {
                  const transfer = transfers?.find((t) => t.debtId === debt.id);
                  const cfg = transfer ? TRANSFER_STATUS_CFG[transfer.status] : null;
                  return (
                    <div key={debt.id} className="flex items-center justify-between p-3 rounded-lg border text-sm">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{debt.creditor}</div>
                        {debt.accountNumber && (
                          <div className="text-xs text-muted-foreground">Acct: {debt.accountNumber}</div>
                        )}
                        {transfer?.stripeTransferId && (
                          <div className="text-[10px] font-mono text-muted-foreground mt-0.5 truncate">{transfer.stripeTransferId}</div>
                        )}
                        {transfer?.failureMessage && (
                          <div className="text-[10px] text-red-600 mt-0.5 truncate" title={transfer.failureMessage}>{transfer.failureMessage}</div>
                        )}
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        <div className="font-semibold">{fmt(debt.effectiveAmount ?? debt.balance)}</div>
                        {cfg ? (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-medium mt-1 ${cfg.color}`}>
                            {cfg.icon}{cfg.label}
                          </span>
                        ) : debt.payoffConfirmationUrl ? (
                          <a href={debt.payoffConfirmationUrl} target="_blank" rel="noreferrer" className="text-xs text-green-600 flex items-center gap-0.5 justify-end">
                            <CheckCircle className="h-3 w-3" /> Confirmed
                          </a>
                        ) : (
                          <span className="text-xs text-muted-foreground flex items-center gap-0.5 justify-end">
                            <Clock className="h-3 w-3" /> Awaiting
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {transfers && transfers.length > 0 && (
                <div className="mt-2 grid grid-cols-4 gap-1 text-center text-[10px]">
                  {(["pending", "in_transit", "paid", "failed"] as const).map((s) => {
                    const n = transfers.filter((t) => t.status === s).length;
                    const cfg = TRANSFER_STATUS_CFG[s];
                    return (
                      <div key={s} className={`rounded px-2 py-1 border ${cfg.color}`}>
                        <div className="font-bold text-sm">{n}</div>
                        <div>{cfg.label}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {batch.status === "sent" && (
              <div className="space-y-3">
                {!allConfirmed && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>
                      Not all debtors have uploaded their payoff confirmations yet. You can still confirm, but best practice is to wait.
                    </span>
                  </div>
                )}

                {confirming ? (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-3">
                    <p className="text-sm font-medium text-red-900">
                      Are you sure? This will mark all {batch.debts.length} debts as paid, send forgiveness letters, and issue IRS tax acknowledgments to all donors.
                    </p>
                    <div className="flex gap-2 justify-end">
                      <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>Cancel</Button>
                      <Button
                        size="sm"
                        onClick={() => confirmMutation.mutate()}
                        disabled={confirmMutation.isPending}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {confirmMutation.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                        ) : (
                          <BadgeCheck className="h-3.5 w-3.5 mr-1" />
                        )}
                        Yes, Confirm Batch
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button className="w-full" onClick={() => setConfirming(true)}>
                    <BadgeCheck className="h-4 w-4 mr-2" />
                    Confirm Payout & Issue Tax Acknowledgments
                  </Button>
                )}
              </div>
            )}

            {confirmResult !== null ? (
              <div className="space-y-3">
                <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-green-800 text-sm space-y-1">
                  <div className="flex items-center gap-2 font-medium">
                    <CheckCircle className="h-4 w-4 shrink-0" />
                    Batch confirmed
                  </div>
                  <ul className="ml-6 list-disc text-sm space-y-0.5">
                    <li>{confirmResult.debtsMarkedPaid} debt{confirmResult.debtsMarkedPaid !== 1 ? "s" : ""} marked paid</li>
                    <li>{confirmResult.donorAcknowledgmentsSent} IRS acknowledgment{confirmResult.donorAcknowledgmentsSent !== 1 ? "s" : ""} sent</li>
                  </ul>
                </div>
                {confirmResult.skipped1099cDebtIds.length > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 text-amber-800 text-sm">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>
                      {confirmResult.skipped1099cDebtIds.length} debt{confirmResult.skipped1099cDebtIds.length !== 1 ? "s" : ""} already had a 1099-C on file and {confirmResult.skipped1099cDebtIds.length !== 1 ? "were" : "was"} not re-notified.
                    </span>
                  </div>
                )}
                <Button variant="outline" className="w-full" onClick={() => { setConfirmResult(null); onClose(); }}>
                  Done
                </Button>
              </div>
            ) : batch.status === "confirmed" && (
              <div className="rounded-lg border border-green-200 bg-green-50 p-3 flex items-center gap-2 text-green-800 text-sm">
                <CheckCircle className="h-4 w-4 shrink-0" />
                Batch confirmed — all debts paid, IRS acknowledgments sent.
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-6">Batch not found.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Reject Dialog ─────────────────────────────────────────────────────────────
interface RejectDialogProps {
  batch: PayoutBatch | null;
  onClose: () => void;
}

function RejectDialog({ batch, onClose }: RejectDialogProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [reason, setReason] = useState("");

  const rejectMutation = useMutation({
    mutationFn: () => rejectPayoutBatch(batch!.id, reason.trim() || undefined),
    onSuccess: () => {
      toast({ title: "Batch rejected", description: `Batch #${batch!.id} has been rejected.` });
      void qc.invalidateQueries({ queryKey: ["payout-batches"] });
      onClose();
      setReason("");
    },
    onError: (err: Error & { data?: { error?: string } }) => {
      toast({ title: "Rejection failed", description: err?.data?.error ?? err.message, variant: "destructive" });
    },
  });

  return (
    <Dialog open={batch !== null} onOpenChange={(o) => { if (!o) { setReason(""); onClose(); } }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reject Batch #{batch?.id}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            Rejecting will cancel this batch. The debts will remain queued and no funds will move.
          </div>
          <div className="space-y-2">
            <Label htmlFor="rejectReason">Reason (optional)</Label>
            <Textarea
              id="rejectReason"
              placeholder="Explain why this batch is being rejected…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => { setReason(""); onClose(); }}>Cancel</Button>
          <Button
            variant="destructive"
            onClick={() => rejectMutation.mutate()}
            disabled={rejectMutation.isPending}
          >
            {rejectMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <XCircle className="h-4 w-4 mr-1" />}
            Reject Batch
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function PayoutBatches() {
  const [, navigate] = useLocation();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailBatchId, setDetailBatchId] = useState<number | null>(null);
  const [approveConfirmBatch, setApproveConfirmBatch] = useState<PayoutBatch | null>(null);
  const [rejectBatch, setRejectBatch] = useState<PayoutBatch | null>(null);
  const { user } = useUser();
  const qc = useQueryClient();
  const { toast } = useToast();

  // Auto-open the "Create Payout Batch" dialog when arriving from an AI suggestion
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("autoOpen") === "true") {
      setCreateOpen(true);
      const clean = window.location.pathname;
      window.history.replaceState({}, "", clean);
    }
  }, []);

  const { data: batches, isLoading } = useQuery<PayoutBatch[]>({
    queryKey: ["payout-batches"],
    queryFn: listPayoutBatches,
    refetchInterval: 30_000,
  });

  const approveMutation = useMutation({
    mutationFn: (id: number) => approvePayoutBatch(id),
    onSuccess: (_, id) => {
      toast({ title: "Batch approved", description: `Batch #${id} approved — payment processing has started.` });
      void qc.invalidateQueries({ queryKey: ["payout-batches"] });
      setApproveConfirmBatch(null);
    },
    onError: (err: Error & { data?: { error?: string } }) => {
      toast({ title: "Approval failed", description: err?.data?.error ?? err.message, variant: "destructive" });
    },
  });

  const pendingApprovalBatches = (batches ?? []).filter((b) => b.status === "pending_approval");
  const sentCount = (batches ?? []).filter((b) => b.status === "sent").length;
  const confirmedTotal = (batches ?? [])
    .filter((b) => b.status === "confirmed")
    .reduce((s, b) => s + b.totalAmount, 0);

  // Current admin's Clerk user ID (used to hide approve/reject for batch creator)
  const currentClerkId = user?.id ?? null;

  return (
    <div className="min-h-screen bg-background">
      <nav className="sticky top-0 z-50 border-b bg-slate-900 border-slate-700">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => navigate("/admin")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Admin Dashboard
            </Button>
            <div className="flex items-center gap-2">
              <LogoMark size={28} />
              <span className="font-semibold text-white">Payout Batches</span>
              <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-600 text-white tracking-wide">ADMIN</span>
              {pendingApprovalBatches.length > 0 && (
                <Badge className="bg-blue-600 text-white border-none text-[10px] px-1.5 py-0">
                  {pendingApprovalBatches.length} pending
                </Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-1" /> New Batch
            </Button>
            <UserButton />
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-5 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="text-2xl font-bold">{batches?.length ?? 0}</div>
                <div className="text-xs text-muted-foreground">Total Batches</div>
              </div>
            </CardContent>
          </Card>
          <Card className={pendingApprovalBatches.length > 0 ? "border-blue-300 bg-blue-50/30" : ""}>
            <CardContent className="pt-5 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <UserCheck className="h-5 w-5 text-blue-700" />
              </div>
              <div>
                <div className="text-2xl font-bold">{pendingApprovalBatches.length}</div>
                <div className="text-xs text-muted-foreground">Pending Approval</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-100">
                <Clock className="h-5 w-5 text-yellow-700" />
              </div>
              <div>
                <div className="text-2xl font-bold">{sentCount}</div>
                <div className="text-xs text-muted-foreground">Awaiting Confirmation</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <DollarSign className="h-5 w-5 text-green-700" />
              </div>
              <div>
                <div className="text-2xl font-bold">{fmt(confirmedTotal)}</div>
                <div className="text-xs text-muted-foreground">Total Confirmed</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Approval Queue */}
        {pendingApprovalBatches.length > 0 && (
          <div className="rounded-xl border border-blue-300 bg-blue-50/40 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-blue-700" />
              <h2 className="font-semibold text-blue-900">Pending Approval — Second Admin Required</h2>
            </div>
            <p className="text-sm text-blue-700">
              These batches are waiting for a second admin's approval. The admin who created a batch cannot approve their own.
            </p>
            <div className="space-y-3">
              {pendingApprovalBatches.map((batch) => {
                const isCreator = currentClerkId === batch.executedBy;
                return (
                  <div key={batch.id} className="rounded-lg border border-blue-200 bg-white p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-1">
                        <div className="font-semibold">Batch #{batch.id} — {fmt(batch.totalAmount)}</div>
                        <div className="text-sm text-muted-foreground">
                          {batch.poolName ?? `Pool #${batch.poolId}`} · {batch.debtsCount} debt{batch.debtsCount !== 1 ? "s" : ""} · {METHOD_LABELS[batch.paymentMethod] ?? batch.paymentMethod}
                        </div>
                        {batch.reference && (
                          <div className="text-xs text-muted-foreground font-mono">Ref: {batch.reference}</div>
                        )}
                        <div className="text-xs text-muted-foreground">Created {fmtDate(batch.createdAt)}</div>
                      </div>
                      <div className="shrink-0">
                        {isCreator ? (
                          <div className="flex items-center gap-1.5 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
                            <Clock className="h-3.5 w-3.5" />
                            Awaiting second approval
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-300 text-red-700 hover:bg-red-50"
                              onClick={() => setRejectBatch(batch)}
                            >
                              <XCircle className="h-3.5 w-3.5 mr-1" />
                              Reject
                            </Button>
                            <Button
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700"
                              onClick={() => setApproveConfirmBatch(batch)}
                            >
                              <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                              Approve
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Approve confirmation dialog */}
        <Dialog open={approveConfirmBatch !== null} onOpenChange={(o) => { if (!o) setApproveConfirmBatch(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Approve Batch #{approveConfirmBatch?.id}?</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="rounded-lg border bg-muted/20 p-3 space-y-1">
                <div>Pool: <span className="font-medium">{approveConfirmBatch?.poolName ?? `#${approveConfirmBatch?.poolId}`}</span></div>
                <div>Amount: <span className="font-medium">{fmt(approveConfirmBatch?.totalAmount ?? 0)}</span></div>
                <div>Debts: <span className="font-medium">{approveConfirmBatch?.debtsCount}</span></div>
                <div>Method: <span className="font-medium">{METHOD_LABELS[approveConfirmBatch?.paymentMethod ?? ""] ?? approveConfirmBatch?.paymentMethod}</span></div>
              </div>
              <p className="text-muted-foreground">
                Approving will immediately move these debts to processing and deduct the amount from the pool balance. This action cannot be undone.
              </p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setApproveConfirmBatch(null)}>Cancel</Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => approveConfirmBatch && approveMutation.mutate(approveConfirmBatch.id)}
                disabled={approveMutation.isPending}
              >
                {approveMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <BadgeCheck className="h-4 w-4 mr-1" />
                )}
                Yes, Approve & Execute
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Batch list */}
        <Card>
          <CardHeader>
            <CardTitle>All Payout Batches</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : !batches?.length ? (
              <div className="text-center py-10 text-muted-foreground">
                <Send className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p>No payout batches yet.</p>
                <p className="text-sm mt-1">Create one when a pool has funded debts ready to pay.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {batches.map((batch) => {
                  const sc = STATUS_BADGE[batch.status] ?? STATUS_BADGE.sent;
                  return (
                    <button
                      key={batch.id}
                      className="w-full text-left p-4 rounded-xl border hover:bg-muted/30 transition-colors"
                      onClick={() => setDetailBatchId(batch.id)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold">Batch #{batch.id}</div>
                          <div className="text-sm text-muted-foreground">
                            {batch.poolName ?? `Pool #${batch.poolId}`} · {batch.debtsCount} debts · {METHOD_LABELS[batch.paymentMethod] ?? batch.paymentMethod}
                          </div>
                          {batch.reference && (
                            <div className="text-xs text-muted-foreground font-mono mt-0.5">Ref: {batch.reference}</div>
                          )}
                          <div className="text-xs text-muted-foreground mt-0.5">{fmtDate(batch.createdAt)}</div>
                          {batch.status === "rejected" && batch.rejectionReason && (
                            <div className="text-xs text-red-600 mt-0.5">Reason: {batch.rejectionReason}</div>
                          )}
                        </div>
                        <div className="text-right shrink-0 space-y-1">
                          <div className="font-bold">{fmt(batch.totalAmount)}</div>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${sc.color}`}>
                            {sc.label}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground text-center">
          Each confirmed batch automatically sends IRS 501(c)(3) tax acknowledgments to all donors and forgiveness letters to debtors.
        </p>
      </div>

      <CreateBatchDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <BatchDetailDialog batchId={detailBatchId} onClose={() => setDetailBatchId(null)} />
      <RejectDialog batch={rejectBatch} onClose={() => setRejectBatch(null)} />
      <QuoteCarousel />

    </div>
  );
}
